import { useEffect, useState } from 'react';

import { ApiError } from '../lib/api';
import type { PaginatedResponse } from '../lib/api';

interface ResourceState<T> {
  status: 'loading' | 'success' | 'error';
  data: PaginatedResponse<T> | null;
  error: ApiError | null;
}

export function usePaginatedResource<T>(
  token: string,
  page: number,
  load: (token: string, page: number, signal?: AbortSignal) => Promise<PaginatedResponse<T>>,
) {
  const [state, setState] = useState<ResourceState<T>>({
    status: 'loading',
    data: null,
    error: null,
  });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setState({ status: 'loading', data: null, error: null });

    load(token, page, controller.signal)
      .then((data) => {
        if (!cancelled) {
          setState({ status: 'success', data, error: null });
        }
      })
      .catch((error) => {
        if (controller.signal.aborted) {
          return;
        }
        if (!cancelled) {
          setState({
            status: 'error',
            data: null,
            error:
              error instanceof ApiError
                ? error
                : new ApiError(String(error), 500, 'request_failed'),
          });
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [load, page, reloadToken, token]);

  return {
    ...state,
    reload() {
      setReloadToken((value) => value + 1);
    },
  };
}
